// absolute imports
import { useMatches } from '@tanstack/react-router';
import PropTypes from 'prop-types';
import { useLayoutEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

// relative imports
import AccountConfigError from '../../error-handling/AccountConfigError';
import ErrorBoundary from '../../error-handling/ErrorBoundary';
import { useAccountConfig } from '../../providers/AccountConfigProvider';
import useAlternativeDisplay from '../custom-hooks/useAlternativeDisplay';
import Header from '../header/Header';
import MobileHeader from '../header/MobileHeader';

/** vh stops for overscroll: primary through header zone, then PageBody grey toward bottom */
const OVERSCROLL_PRIMARY_END_VH = 32;
const OVERSCROLL_GREY_FROM_VH = 44;

function snapshotBackground(el) {
  return {
    backgroundColor: el.style.backgroundColor,
    backgroundImage: el.style.backgroundImage,
    backgroundAttachment: el.style.backgroundAttachment,
    backgroundRepeat: el.style.backgroundRepeat,
  };
}

function restoreBackground(el, prev) {
  el.style.backgroundColor = prev.backgroundColor;
  el.style.backgroundImage = prev.backgroundImage;
  el.style.backgroundAttachment = prev.backgroundAttachment;
  el.style.backgroundRepeat = prev.backgroundRepeat;
}

function getOverscrollChromeStyles(theme) {
  const primary =
    theme.whiteLabel.PageHeader.color || theme.palette.primary.main;
  const grey =
    theme.whiteLabel.PageBody.color || theme.palette.grey[200];
  const backgroundImage = `linear-gradient(180deg, ${primary} 0%, ${primary} ${OVERSCROLL_PRIMARY_END_VH}vh, ${grey} ${OVERSCROLL_GREY_FROM_VH}vh, ${grey} 100%)`;

  return {
    backgroundColor: grey,
    backgroundImage,
    backgroundAttachment: 'fixed',
    backgroundRepeat: 'no-repeat',
  };
}

const useStyles = makeStyles()((theme, { headerHeight, hideHeader }) => ({
  mainContent: {
    position: 'fixed',
    top: hideHeader ? 0 : headerHeight || 56,
    bottom: 0,
    width: '100%',
    overflowY: 'scroll',
    overscrollBehaviorY: 'contain',
    ...getOverscrollChromeStyles(theme),
  },
}));

const PrimaryLayout = ({ children }) => {
  const theme = useTheme();
  const matches = useMatches();
  const { staticData } = matches[matches.length - 1];
  const {
    hideSecondaryMenu,
    hideAccountMenu,
    hideHeader,
    hideUpdateButton,
    accountOptional,
  } = staticData;
  const { isMobile } = useAlternativeDisplay();
  const [headerHeight, setHeaderHeight] = useState(() => {
    if (isMobile) return 57;
    if (hideHeader) return 0;
    if (hideSecondaryMenu) return 56;
    return 104;
  });
  const { classes } = useStyles({ isMobile, hideHeader, headerHeight });

  useLayoutEffect(() => {
    const chrome = getOverscrollChromeStyles(theme);
    const html = document.documentElement;
    const body = document.body;
    const app = document.getElementById('app');
    const prevHtml = snapshotBackground(html);
    const prevBody = snapshotBackground(body);
    const prevApp = app ? snapshotBackground(app) : null;

    Object.assign(html.style, chrome);
    Object.assign(body.style, chrome);
    if (app) Object.assign(app.style, chrome);

    return () => {
      restoreBackground(html, prevHtml);
      restoreBackground(body, prevBody);
      if (app && prevApp) restoreBackground(app, prevApp);
    };
  }, [theme]);

  const accountConfigData = useAccountConfig();
  const { configError } = accountConfigData || {};

  return (
    <>
      {!hideHeader && !isMobile && (
        <Header
          hideAccountMenu={hideAccountMenu}
          hideSecondaryMenu={hideSecondaryMenu || !!configError}
          hideUpdateButton={hideUpdateButton}
          accountOptional={accountOptional}
          setHeaderHeight={setHeaderHeight}
        />
      )}
      {!hideHeader && isMobile && (
        <MobileHeader
          hideAccountMenu={hideAccountMenu}
          hideUpdateButton={hideUpdateButton}
          setHeaderHeight={setHeaderHeight}
        />
      )}
      <ErrorBoundary
        fallbackType="modal"
        errorMsg="Uncaught error in layout"
        context={{
          component: PrimaryLayout.displayName,
        }}
      >
        <div className={classes.mainContent}>
          {configError ? <AccountConfigError /> : children}
        </div>
      </ErrorBoundary>
    </>
  );
};

export default PrimaryLayout;

PrimaryLayout.propTypes = {
  children: PropTypes.node.isRequired,
};